import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';
import { useApp } from '../context/AppContext';

const VideoPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useApp();
    const [video, setVideo] = useState(null);
    const [relatedVideos, setRelatedVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLiked, setIsLiked] = useState(false);
    const [hasTrackedView, setHasTrackedView] = useState(false);

    useEffect(() => {
        fetchVideo();
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        if (video && !hasTrackedView) {
            trackView();
        }
    }, [video]);

    const fetchVideo = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/videos/${id}`);
            setVideo(res.data);

            const relatedRes = await api.get(`/videos?category=${res.data.category}&limit=4`);
            const filtered = relatedRes.data.filter(v => v._id !== res.data._id);
            setRelatedVideos(filtered.slice(0, 4));

            const likedVideos = JSON.parse(localStorage.getItem('likedVideos') || '[]');
            setIsLiked(likedVideos.includes(res.data._id));

        } catch (error) {
            console.error('Error fetching video:', error);
            showToast('Video not found', 'error');
            navigate('/videos');
        } finally {
            setLoading(false);
        }
    };

    const trackView = async () => {
        if (hasTrackedView) return;

        const sessionKey = `video_viewed_${id}`;
        if (sessionStorage.getItem(sessionKey)) {
            console.log('Already counted in this session');
            return;
        }

        const viewedVideos = JSON.parse(localStorage.getItem('viewedVideos') || '[]');
        if (viewedVideos.includes(id)) {
            console.log('Already counted previously');
            return;
        }

        try {
            setHasTrackedView(true);
            sessionStorage.setItem(sessionKey, 'true');
            viewedVideos.push(id);
            localStorage.setItem('viewedVideos', JSON.stringify(viewedVideos));
            const res = await api.post(`/videos/${id}/view`);
            setVideo(prev => ({ ...prev, views: res.data.views }));
            console.log('✅ Video view counted! New count:', res.data.views);
        } catch (error) {
            console.error('Error tracking view:', error);
        }
    };

    const handleLike = async () => {
        if (!video) return;

        try {
            if (!isLiked) {
                await api.post(`/videos/${video._id}/like`);
                const likedVideos = JSON.parse(localStorage.getItem('likedVideos') || '[]');
                likedVideos.push(video._id);
                localStorage.setItem('likedVideos', JSON.stringify(likedVideos));
                setIsLiked(true);
                setVideo({ ...video, likes: (video.likes || 0) + 1 });
                showToast('👍 You liked this video!');
            } else {
                showToast('You already liked this video', 'info');
            }
        } catch (error) {
            console.error('Error liking video:', error);
        }
    };

    const handleShare = async () => {
        const shareUrl = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: video.title,
                    text: video.description,
                    url: shareUrl,
                });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                showToast('📋 Video link copied to clipboard!');
            }
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    const formatDuration = (duration) => {
        if (!duration) return '—';
        if (duration.includes(':')) return duration;
        const mins = Math.floor(parseInt(duration) / 60);
        const secs = parseInt(duration) % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Recently';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    };

    const getYouTubeEmbedUrl = (url) => {
        if (!url) return '';
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return `https://www.youtube.com/embed/${match[1]}?autoplay=0&rel=0&modestbranding=1`;
            }
        }
        return url;
    };

    const isYouTubeUrl = (url) => {
        return url && (url.includes('youtube.com') || url.includes('youtu.be'));
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    const generateVideoSchema = () => {
        if (!video) return null;
        return {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": video.seo?.metaTitle || video.title,
            "description": video.seo?.metaDescription || video.description || stripHtml(video.content || '').substring(0, 200),
            "thumbnailUrl": video.thumb || video.thumbData,
            "uploadDate": video.createdAt,
            "duration": video.dur,
            "contentUrl": video.url,
            "embedUrl": getYouTubeEmbedUrl(video.url),
            "viewCount": video.views || 0,
            "interactionStatistic": {
                "@type": "InteractionCounter",
                "interactionType": "https://schema.org/LikeAction",
                "userInteractionCount": video.likes || 0
            }
        };
    };

    if (loading) {
        return (
            <div className="loader" style={{ minHeight: '60vh' }}>
                <div className="loader-spinner"></div>
            </div>
        );
    }

    if (!video) {
        return (
            <div className="page" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <h2>🎬 Video Not Found</h2>
                <p>The video you're looking for doesn't exist or has been removed.</p>
                <button className="btn btn-red" onClick={() => navigate('/videos')} style={{ marginTop: '20px' }}>
                    Browse Videos →
                </button>
            </div>
        );
    }

    const embedUrl = video.videoSEO?.embedUrl || getYouTubeEmbedUrl(video.url);

    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>{video.seo?.metaTitle || video.title} | Wistar News Videos</title>
                <meta name="description" content={video.seo?.metaDescription || video.description || stripHtml(video.content || '').substring(0, 160)} />
                <meta name="keywords" content={video.seo?.metaKeywords || video.category} />
                <link rel="canonical" href={video.seo?.canonicalUrl || window.location.href} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="video.other" />
                <meta property="og:title" content={video.seo?.ogTitle || video.seo?.metaTitle || video.title} />
                <meta property="og:description" content={video.seo?.ogDescription || video.seo?.metaDescription || video.description || stripHtml(video.content || '').substring(0, 160)} />
                <meta property="og:image" content={video.seo?.ogImage || video.thumb || video.thumbData} />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:site_name" content="Wistar News" />
                <meta property="og:video" content={video.url} />
                <meta property="og:video:duration" content={video.dur} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="player" />
                <meta name="twitter:title" content={video.seo?.twitterTitle || video.seo?.metaTitle || video.title} />
                <meta name="twitter:description" content={video.seo?.twitterDescription || video.seo?.metaDescription || video.description || stripHtml(video.content || '').substring(0, 160)} />
                <meta name="twitter:image" content={video.seo?.twitterImage || video.thumb || video.thumbData} />
                <meta name="twitter:player" content={embedUrl} />
                <meta name="twitter:player:width" content="640" />
                <meta name="twitter:player:height" content="360" />

                {/* JSON-LD Schema */}
                <script type="application/ld+json">
                    {JSON.stringify(generateVideoSchema())}
                </script>
            </Helmet>

            <div className="page" style={{ paddingTop: '20px' }}>
                <button
                    onClick={() => navigate('/videos')}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: 'var(--navy)',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                    }}
                >
                    ← Back to Videos
                </button>

                <div style={{
                    background: '#000',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    marginBottom: '24px',
                    aspectRatio: '16/9',
                    position: 'relative'
                }}>
                    {embedUrl && isYouTubeUrl(video.url) ? (
                        <iframe
                            src={embedUrl}
                            title={video.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ width: '100%', height: '100%' }}
                        />
                    ) : (
                        <video
                            src={video.url}
                            controls
                            autoPlay
                            style={{ width: '100%', height: '100%' }}
                            poster={video.thumb || video.thumbData}
                        />
                    )}
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <h1 style={{ fontSize: '28px', marginBottom: '12px', color: 'var(--navy)' }}>
                        {video.title}
                    </h1>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '20px',
                        paddingBottom: '16px',
                        borderBottom: '1px solid var(--lgray)',
                        marginBottom: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>📅</span>
                            <span style={{ fontSize: '14px', color: '#666' }}>{formatDate(video.date || video.createdAt)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>👁️</span>
                            <span style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>
                                {video.views?.toLocaleString() || 0} views
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>⏱️</span>
                            <span style={{ fontSize: '14px', color: '#666' }}>{formatDuration(video.dur)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>📂</span>
                            <span style={{ fontSize: '14px', color: '#666' }}>{video.category}</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <button
                            onClick={handleLike}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 20px',
                                background: isLiked ? '#ff6b6b' : '#f0f0f0',
                                color: isLiked ? '#fff' : '#333',
                                border: 'none',
                                borderRadius: '30px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                            }}
                        >
                            👍 {video.likes || 0} Likes
                        </button>
                        <button
                            onClick={handleShare}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 20px',
                                background: '#f0f0f0',
                                color: '#333',
                                border: 'none',
                                borderRadius: '30px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600'
                            }}
                        >
                            📤 Share
                        </button>
                    </div>

                    {video.description && (
                        <div style={{
                            background: '#f8f9fa',
                            padding: '20px',
                            borderRadius: '12px',
                            marginBottom: '24px'
                        }}>
                            <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>📝 Description</h3>
                            <p style={{ lineHeight: '1.7', color: '#444', whiteSpace: 'pre-line' }}>
                                {video.description}
                            </p>
                        </div>
                    )}

                    {video.videoSEO?.transcript && (
                        <details style={{ marginBottom: '24px' }}>
                            <summary style={{
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                padding: '10px 0',
                                color: 'var(--navy)'
                            }}>
                                📜 Show Transcript
                            </summary>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '12px',
                                marginTop: '10px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                fontSize: '14px',
                                lineHeight: '1.6'
                            }}>
                                {video.videoSEO.transcript}
                            </div>
                        </details>
                    )}
                </div>

                {relatedVideos.length > 0 && (
                    <div style={{ marginTop: '40px' }}>
                        <h2 className="section-title">🎬 Related Videos</h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '20px',
                            marginTop: '20px'
                        }}>
                            {relatedVideos.map(related => (
                                <div
                                    key={related._id}
                                    onClick={() => navigate(`/video/${related._id}`)}
                                    style={{
                                        background: '#fff',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    <div style={{ position: 'relative', aspectRatio: '16/9' }}>
                                        <img
                                            src={related.thumb || related.thumbData || 'https://via.placeholder.com/400x225'}
                                            alt={related.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => e.target.src = 'https://via.placeholder.com/400x225?text=No+Image'}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '8px',
                                            right: '8px',
                                            background: 'rgba(0,0,0,0.7)',
                                            color: '#fff',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontSize: '11px'
                                        }}>
                                            {formatDuration(related.dur)}
                                        </div>
                                    </div>
                                    <div style={{ padding: '12px' }}>
                                        <h4 style={{
                                            fontSize: '14px',
                                            marginBottom: '6px',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            {related.title}
                                        </h4>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '11px',
                                            color: '#888'
                                        }}>
                                            <span>{related.views?.toLocaleString() || 0} views</span>
                                            <span>{formatDate(related.date || related.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default VideoPage;